import { FieldAPI, SearchAPI } from "@ai360/core";
import { OrderedMap } from "immutable";
import { FieldInfo } from "~/customer-data/models";
import { IField, ISummary as IApiSummary } from "~/utils/api/summary";
import { AgEventSummary } from "./events/models";
import { IRecsEventsReducerActions } from "./interfaces";
import { RecSummary } from "./recs/model";
import { IBatchRecEventChange } from "./reducer";
export const BATCH_TEMPLATE_FIELD_GUID = "BATCH_TEMPLATE";
export const MAX_FETCH_FIELDS = 5000;

export interface IChangedMessage {
    fieldGuid: string;
}

export interface IRecChangedMessage extends IChangedMessage {
    recGeneralGuid: string;
    recData: FieldAPI.IRecSummary;
}

export interface IEventChangedMessage extends IChangedMessage {
    agEventGeneralGuid: string;
    eventData: Partial<FieldAPI.IAgEventSummary>;
}

export interface IEventStatusMessage extends IChangedMessage {
    agEventGeneralGuid: string;
    eventStatusCode: number;
    fieldGuid: string;
}

export interface IFetchImplementation<TApiSummary extends IApiSummary, TSummary> {
    apiCall: (fieldGuids: string[]) => Promise<TApiSummary[]>;
    parseField: (field: IField<TApiSummary>) => TSummary[];
    withParsedFields?: (activeMap: OrderedMap<string, TSummary[]>) => any;
    successAction: (
        active: OrderedMap<string, TSummary[]>,
        inactive: OrderedMap<string, TSummary[]>,
        activeField: OrderedMap<string, SearchAPI.IFieldResult>,
        inactiveField: OrderedMap<string, SearchAPI.IFieldResult>
    ) => any;
}

export const getNewFieldRecEventList = (
    change: IBatchRecEventChange,
    existingFieldRecEventList: RecSummary[] | AgEventSummary[],
    fields: OrderedMap<string, FieldInfo>,
    { dateProp, keyProp, fromJsonObj }: IRecsEventsReducerActions
): RecSummary[] | AgEventSummary[] => {
    const { fieldGuid, recEventGeneralGuid, recEventData } = change;
    const newFieldRecEventList = [...existingFieldRecEventList];
    const existingRecEventIndex = newFieldRecEventList.findIndex(
        (fieldRecEvent) => fieldRecEvent[keyProp] === recEventGeneralGuid
    );
    if (recEventData != null) {
        const field = fields.get(fieldGuid);
        const existingRecEvent =
            existingRecEventIndex !== -1 ? newFieldRecEventList[existingRecEventIndex] : null;
        const mergedRecEvent = existingRecEvent ? { ...existingRecEvent } : {};
        Object.assign(
            mergedRecEvent,
            Object.fromEntries(Object.entries(recEventData).filter(([_, value]) => value !== null))
        );
        const newRecEvent = fromJsonObj(
            mergedRecEvent as unknown as FieldAPI.IAgEventSummary | FieldAPI.IRecSummary,
            field
        );
        if (existingRecEventIndex !== -1) {
            newFieldRecEventList.splice(existingRecEventIndex, 1, newRecEvent);
        } else {
            newFieldRecEventList.push(newRecEvent);
        }
    } else if (existingRecEventIndex !== -1) {
        newFieldRecEventList.splice(existingRecEventIndex, 1);
    } else {
        return existingFieldRecEventList;
    }
    const asString = (v: unknown) => String(v ?? "");
    return newFieldRecEventList.sort(
        (a, b) =>
            asString(a.croppingSeasonName).localeCompare(asString(b.croppingSeasonName)) ||
            asString(a[dateProp]).localeCompare(asString(b[dateProp]))
    ) as RecSummary[] | AgEventSummary[];
};

export const isBatchStatusOutOfOrder = (
    existingFieldRecEventList,
    recEventData,
    keyProp: string,
    recEventGeneralGuid: string,
    _batchFieldGuid: string
): boolean => {
    const existingRecEvent = existingFieldRecEventList?.find(
        (fieldRecEvent) => fieldRecEvent[keyProp] === recEventGeneralGuid
    );
    if (!recEventData || !existingRecEvent) {
        return false;
    }
    const recEventWasCancelled = Number(existingRecEvent?.importedStatus) === 999;
    return !existingRecEvent || !recEventData
        ? false
        : recEventWasCancelled ||
              ((Number(recEventData.importedStatus) === 8 ||
                  Number(recEventData.importedStatus) === 30) &&
                  Number(existingRecEvent.importedStatus) === 6);
};

export const removeDuplicateChanges = (changes: IBatchRecEventChange[]) => {
    const recEventGuidToChangeMap = new Map<string, IBatchRecEventChange>();
    changes.forEach((change) => {
        const existingChange = recEventGuidToChangeMap.get(change.recEventGeneralGuid);
        if (
            !existingChange ||
            change.recEventData.importedStatus < existingChange.recEventData.importedStatus
        ) {
            recEventGuidToChangeMap.set(change.recEventGeneralGuid, change);
        }
    });
    return Array.from(recEventGuidToChangeMap.values());
};
